import type {
  ConvertRequestInput,
  ConvertResult,
  Draft,
  EffectiveEntitlement,
  HistoryEntry,
  MobileSession,
  Session,
} from "@fluentry/domain";
import { parseEnvelope, requestJson } from "./http";

type ApiSuccess<T> = {
  success: true;
  result: T;
};

type ApiFailure = {
  success: false;
  error: {
    code: string;
    message: string;
  };
};

type ApiRequestOptions = {
  mobileSession?: MobileSession | null;
};

function buildHeaders(options?: ApiRequestOptions): HeadersInit | undefined {
  if (!options?.mobileSession?.token) {
    return undefined;
  }

  return {
    authorization: `Bearer ${options.mobileSession.token}`,
  };
}

export async function fetchSession(
  apiBaseUrl: string,
  options?: ApiRequestOptions,
): Promise<Session> {
  const payload = await requestJson<ApiSuccess<Session> | ApiFailure>("/api/auth/web/session", {
    apiBaseUrl,
    ...(buildHeaders(options) ? { headers: buildHeaders(options) } : {}),
  });
  return parseEnvelope(payload);
}

export async function requestTrialConvert(
  apiBaseUrl: string,
  request: ConvertRequestInput,
): Promise<ConvertResult> {
  const payload = await requestJson<ApiSuccess<ConvertResult> | ApiFailure>("/api/trial/convert", {
    apiBaseUrl,
    method: "POST",
    bodyJson: request as unknown as Record<string, unknown>,
  });
  return parseEnvelope(payload);
}

export async function requestAppConvert(
  apiBaseUrl: string,
  request: ConvertRequestInput,
  options?: ApiRequestOptions,
): Promise<ConvertResult> {
  const payload = await requestJson<ApiSuccess<ConvertResult> | ApiFailure>("/api/app/convert", {
    apiBaseUrl,
    method: "POST",
    headers: {
      ...buildHeaders(options),
    },
    bodyJson: request as unknown as Record<string, unknown>,
  });
  return parseEnvelope(payload);
}

export async function fetchHistory(
  apiBaseUrl: string,
  options?: ApiRequestOptions,
): Promise<HistoryEntry[]> {
  const payload = await requestJson<ApiSuccess<HistoryEntry[]> | ApiFailure>("/api/app/history", {
    apiBaseUrl,
    ...(buildHeaders(options) ? { headers: buildHeaders(options) } : {}),
  });
  return parseEnvelope(payload);
}

export async function fetchDrafts(
  apiBaseUrl: string,
  options?: ApiRequestOptions,
): Promise<Draft[]> {
  const payload = await requestJson<ApiSuccess<Draft[]> | ApiFailure>("/api/app/drafts", {
    apiBaseUrl,
    ...(buildHeaders(options) ? { headers: buildHeaders(options) } : {}),
  });
  return parseEnvelope(payload);
}

export async function fetchEffectiveEntitlement(
  apiBaseUrl: string,
  options?: ApiRequestOptions,
): Promise<EffectiveEntitlement> {
  const payload = await requestJson<ApiSuccess<EffectiveEntitlement> | ApiFailure>(
    "/api/app/entitlements",
    {
      apiBaseUrl,
      ...(buildHeaders(options) ? { headers: buildHeaders(options) } : {}),
    },
  );
  return parseEnvelope(payload);
}

export async function saveDraft(
  apiBaseUrl: string,
  draftId: string,
  draft: Draft,
  options?: ApiRequestOptions,
): Promise<Draft> {
  const payload = await requestJson<ApiSuccess<Draft> | ApiFailure>(`/api/app/drafts/${draftId}`, {
    apiBaseUrl,
    method: "PUT",
    headers: {
      ...buildHeaders(options),
    },
    bodyJson: draft as unknown as Record<string, unknown>,
  });
  return parseEnvelope(payload);
}

export async function deleteDraft(
  apiBaseUrl: string,
  draftId: string,
  options?: ApiRequestOptions,
): Promise<{ id: string }> {
  const payload = await requestJson<ApiSuccess<{ id: string }> | ApiFailure>(
    `/api/app/drafts/${draftId}`,
    {
      apiBaseUrl,
      method: "DELETE",
      headers: {
        ...buildHeaders(options),
      },
    },
  );
  return parseEnvelope(payload);
}

export async function deleteHistoryEntry(
  apiBaseUrl: string,
  historyEntryId: string,
  options?: ApiRequestOptions,
): Promise<{ id: string }> {
  const payload = await requestJson<ApiSuccess<{ id: string }> | ApiFailure>(
    `/api/app/history/${historyEntryId}`,
    {
      apiBaseUrl,
      method: "DELETE",
      headers: {
        ...buildHeaders(options),
      },
    },
  );
  return parseEnvelope(payload);
}
